import React, { memo, useCallback, useContext } from 'react';
import { Modal, message, Button, Tooltip } from 'antd';
import {
  ExclamationCircleOutlined,
  FileSearchOutlined,
  FolderOpenOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from '@services/api';
import rolesConstants from '@utils/rolesConstants';
import RoleChecker from '@components/RoleChecker';
import SessionContext from '@providers/Session/SessionContext';
import { useNavigate } from 'react-router-dom';

const { confirm } = Modal;

interface IAdminActionsRequest {
  data: any;
  mutate: Function;
  item: any;
  showArchived: boolean;
}

const AdminActionsRequest: React.FC<IAdminActionsRequest> = ({
  data,
  mutate,
  item,
  showArchived,
}) => {
  const { t } = useTranslation('requests');
  const { selectedApp } = useContext(SessionContext);
  const navigate = useNavigate();

  const archiveAccount = useCallback(
    (id: string | number) =>
      api.patch(`/service-proxy/broker/virtual-account/${id}/archive`),
    [],
  );

  const showArchiveConfirm = useCallback(
    (requestId: string | number, requestName: any) => {
      confirm({
        title: t('archiveModal.title'),
        icon: <ExclamationCircleOutlined />,
        content: t('archiveModal.alert', { requestName }),
        centered: true,
        okText: t('archiveModal.yes'),
        cancelText: t('archiveModal.no'),
        okButtonProps: { type: 'primary', danger: true },
        afterClose: () => document.body.style.removeProperty('overflow'),
        onOk: () =>
          archiveAccount(requestId)
            .then(() => {
              mutate({
                ...data,
                content: [
                  ...data.content.filter(
                    (request: any) => request.id !== requestId,
                  ),
                ],
              });
              message.success(t('modal.notifications.successArchiving'));
            })
            .catch(({ response: { status } }) => {
              if (status === 409) {
                message.error(t('errors.archivingWithDependency'));
              } else {
                message.error(t('errors.archiving'));
              }
            }),
      });
    },
    [archiveAccount, t, data, mutate],
  );

  return (
    <>
      <Tooltip title={t('detailsButton')}>
        <Button
          icon={<FileSearchOutlined />}
          disabled={!item?.id}
          onClick={() => {
            navigate(
              `/${selectedApp}/mobile-network/financial/requests/${item.id}`,
            );
          }}
        />
      </Tooltip>
      {showArchived && (
        <RoleChecker role={rolesConstants.MANAGE_REQUESTS_MOT}>
          <Tooltip title={t('toFileButton')}>
            <Button
              icon={<FolderOpenOutlined />}
              danger
              onClick={() => showArchiveConfirm(item.id, item.name)}
              style={{ marginLeft: 8 }}
            />
          </Tooltip>
        </RoleChecker>
      )}
    </>
  );
};

export default memo(AdminActionsRequest);
